import { Injectable } from '@angular/core';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { REPORT_ORIENTATION_MAP } from '../../../models/reports/ReportOrientationMap';
import { SolLaborReportsService } from '../labor-reports/sol-labor-reports.service';
import { SolCostReportService } from '../cost-reports/sol-cost-report.service';
import { SolSubContractorMiscellaneousService } from '../subcontractor-miscellaneous-reports/sol-sub-contractor-miscellaneous.service';
import { SolSystemsReportService } from '../system-reports/sol-systems-report.service';
import { SolMaterialService } from '../material-reports/sol-material.service';
import { SolChecklistBaseService } from '../check-list-reports/sol-checklist-base.service';
import { FireEstimatingService } from '../fire-estimating/fire-estimating.service';
import { TaskBasedLaborService } from '../task-based-labor/task-based-labor.service';
import { ElectricalReportService } from '../electrical-service/electrical-report.service';
import { SolMaterialByAreaService } from '../material-by-area/sol-material-by-area.service';
import { SolMaterialByCountryService } from '../material-by-country/sol-material-by-country.service';
import { SolMaterialRelatedReportsService } from '../material-related-reports/sol-material-related-reports.service';
import { WiringTypesService } from '../wiring-types/wiring-types.service';
import { SpecialPricingReportService } from '../special-pricing-reports/special-pricing-report.service';
import { EstimateOverviewReportService } from '../estimate-overview-report/estimate-overview-report.service';
import { SolGraphicsReportService } from '../sol-graphics-report/sol-graphics-report.service';
import { LaborByLevelService } from '../labor-by-level/labor-by-level.service';
import { CostByLevelService } from '../cost-by-level/cost-by-level.service';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { ReportOrientation } from '../../../models/reports/ReportOrientation';
import { CommonFunctionsService } from '../../common-functions.service';
import { INTERNAL_REPORT_MAP } from '../../../models/reports/InternalReportMap';

@Injectable()
export class ReportDispatcherService {

  constructor(
    private readonly reportsCommonService: ReportsCommonService,
    private readonly solLaborReportsService: SolLaborReportsService,
    private readonly solCostReportService: SolCostReportService,
    private readonly solSubContractorMiscellaneousService: SolSubContractorMiscellaneousService,
    private readonly solSystemReportsService: SolSystemsReportService,
    private readonly solMaterialReportsService: SolMaterialService,
    private readonly solChecklistBaseService: SolChecklistBaseService,
    private readonly fireEstimatingService: FireEstimatingService,
    private readonly taskBasedLaborService: TaskBasedLaborService,
    private readonly electricalReportService: ElectricalReportService,
    private readonly solMaterialRelatedReportsService: SolMaterialRelatedReportsService,
    private readonly solMaterialByAreaService: SolMaterialByAreaService,
    private readonly solMaterialByCountryService: SolMaterialByCountryService,
    private readonly wiringTypesService: WiringTypesService,
    private readonly specialPricingService: SpecialPricingReportService,
    private readonly estimateOverviewReportService: EstimateOverviewReportService,
    private readonly solGraphicsReportService: SolGraphicsReportService,
    private readonly laborByLevelService: LaborByLevelService,
    private readonly costByLevelService: CostByLevelService
  ) {}

  private getOrientation(reportId: ReportList): 'portrait' | 'landscape' {
    const isExpanded = this.reportsCommonService.getReportsCheckboxes()?.expanded?.checked ?? false;
    const orientation: ReportOrientation = REPORT_ORIENTATION_MAP[reportId] ?? { standard: 'portrait', expanded: 'portrait' };
    return isExpanded ? orientation.expanded : orientation.standard;
  }
  private GetInternalReportStatus(reportId: ReportList): boolean {
    const internalReport = INTERNAL_REPORT_MAP[reportId];
    return internalReport ?? false;
  }

  generateReportByName(reportId: ReportList): void {
    const orientation = this.getOrientation(reportId);
    const internalReport = this.GetInternalReportStatus(reportId);
    switch (reportId) {
      case ReportList.EstimateSummaryReportViewModel:
      case ReportList.ZeroCostMaterialByPartNumberViewModel:
      case ReportList.CostByPartNumberByTypeExpandedViewModel:
      case ReportList.CostByPartNumberExpandedViewModel:
        this.solCostReportService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.CostByLevelViewModel:
        this.costByLevelService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.EstimateMaterialBreakdownViewModel:
      case ReportList.MaterialWithNotesByAreaViewModel:
        this.solMaterialByAreaService.generateReport(reportId, orientation, internalReport);
        break;
      case ReportList.PartsByCountryViewModel:
        this.solMaterialByCountryService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.SubcontrctorAndMiscCostViewModel:
      case ReportList.SubContractorMiscNotesViewModel:
        this.solSubContractorMiscellaneousService.generateReport(reportId, orientation, internalReport);
        break;
      case ReportList.LaborByLevelReportViewModel:
        this.laborByLevelService.generateReport(reportId, orientation, internalReport);
        break;
      case ReportList.MaterialTakeOffAndExtensionReportViewModel:
      case ReportList.LaborHoursBySalesOfficeReportViewModel:
      case ReportList.LaborHoursDistViewModel:
      case ReportList.TaskByLevelWithNotesViewModel:
      case ReportList.TaskByLevelReportViewModel:
        this.solLaborReportsService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.SystemsReportViewModel:
      case ReportList.SystemNotesViewModel:
        this.solSystemReportsService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.MaterialViewModel:
      case ReportList.ControllerMaterialViewModel:
        this.solMaterialReportsService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.CostReviewCheckListReportViewModel:
        this.solChecklistBaseService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.MaterialByPartTypeViewModel:
      case ReportList.MaterialByTypeViewModel:
      case ReportList.ServiceContractMaterialViewModel:
        this.solMaterialRelatedReportsService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.SpecialPricingViewModel:
        this.specialPricingService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.SubmittalReportViewModel:
        this.estimateOverviewReportService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.WiringUsedViewModel:
        this.wiringTypesService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.FireEstimatingAnalyticsReportViewModel:
        this.fireEstimatingService.generateReport(reportId, orientation, internalReport);
        break;
      case ReportList.TaskBasedLaborByAreaViewModel:
      case ReportList.TaskBasedLaborSummaryViewModel:
        this.taskBasedLaborService.generateReport(reportId, orientation , internalReport);
        break;
      case ReportList.ElectricalServicesMaterialAndHoursViewModel:
        this.electricalReportService.generateReport(reportId, orientation, internalReport);
        break;
      case ReportList.UsedSymbolViewModel:
      case ReportList.ApplicationDetailViewModel:
        this.solGraphicsReportService.generateReport(reportId, orientation , internalReport);
        break;
      default:
        break;
    }
  }
}
